import React, { useEffect } from "react";
import {
  ListChecks,
  LayoutGrid,
  Users,
  ListTree,
  StickyNote,
  History,
  Receipt,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../component/Navbar/headermenu";
import CustomFooter from "../component/Footer/customFooter";
import { Helmet } from "react-helmet";

import "../assets/css/TaskManagementFeature.css";

import tableView from "../assets/new-images/t1.png";
import taskPeek from "../assets/new-images/t2.png";
import calendarView from "../assets/new-images/t3.png";
import capsuleView from "../assets/new-images/t4.png";
import billFlow from "../assets/new-images/t5.png";
import taskFullPeek from "../assets/new-images/t6.png";

const features = [
  {
    icon: <LayoutGrid size={26} />,
    title: "Multiple Views",
    description:
      "Switch between Table, Capsule, and Calendar views for the same task list, whichever fits how you think.",
  },
  {
    icon: <Users size={26} />,
    title: "My Tasks & Tasks (Everyone)",
    description:
      "Work from your own queue, or switch to Tasks (Everyone) for full visibility across the team — built for managers and admins.",
  },
  {
    icon: <ListTree size={26} />,
    title: "Sub-tasks",
    description:
      "Break any task into sub-tasks without losing track of the parent task.",
  },
  {
    icon: <StickyNote size={26} />,
    title: "Notes on Every Task",
    description:
      "Attach notes directly to a task, so context stays with the work instead of a separate chat thread.",
  },
  {
    icon: <History size={26} />,
    title: "Task & Client History",
    description:
      "A full history of every change on a task, and every task tied to a client — so nothing gets lost after the fact.",
  },
  {
    icon: <Receipt size={26} />,
    title: "Client-Linked Billing",
    description:
      "Closed tasks flow straight into Bill Flow, billed or unbilled, broken down by client.",
  },
];

const viewsShowcase = [
  {
    eyebrow: "Table View",
    title: "Every task, sortable and filterable like a spreadsheet",
    description:
      "Project, task, client, status, period, financial year, and assignees — in one table, with saved tabs like Under Review, GST, and Income Tax so you're never rebuilding the same filter twice.",
    image: tableView,
    alt: "Simplifyte Table View showing tasks with project, task, client, status, period, financial year, and assignee columns",
  },
  {
    eyebrow: "Capsule View",
    title: "A card for every task, built for scanning fast",
    description:
      "Client & group, due date, expiry, assignees, and remarks laid out on one card per task — easier to scan than a dense table when you just need to move fast.",
    image: capsuleView,
    alt: "Simplifyte Capsule View showing tasks as cards with client, due date, expiry, assignees, and remarks",
  },
  {
    eyebrow: "Calendar View",
    title: "See deadlines the moment they land on a date",
    description:
      "Due and closed counts on every day of the month, an overdue count you can't miss, and today's due tasks listed right alongside the calendar.",
    image: calendarView,
    alt: "Simplifyte Calendar View showing a monthly calendar with due and closed task counts per day, an overdue banner, and today's due tasks",
  },
];

const detailShowcase = [
  {
    eyebrow: "Task Detail",
    title: "Open any task without leaving the list",
    description:
      "Client, financial year, period, due date, expiry, assignee, time log, and remarks — all in a side peek panel, so checking one task doesn't mean losing your place in the list.",
    image: taskPeek,
    alt: "Simplifyte task peek panel showing basic task details including client, financial year, period, due date, assignee, and time log",
  },
  {
    eyebrow: "Notes, Sub-tasks & History",
    title: "Every note, sub-task, and change, in one panel",
    description:
      "Expand the same peek panel to add notes, break the task into sub-tasks, and review its full history — plus the client's history — without ever opening a separate screen.",
    image: taskFullPeek,
    alt: "Simplifyte task peek panel expanded to show basic task details, notes, and sub-tasks sections",
  },
  {
    eyebrow: "Client Billing",
    title: "Closed tasks become billing-ready, automatically",
    description:
      "Bill Flow shows billed, unbilled, and non-billable closed tasks by client, so finishing the work and billing for it are never two disconnected steps.",
    image: billFlow,
    alt: "Simplifyte Bill Flow showing billed, unbilled, and non-billable closed tasks broken down by client",
  },
];

const benefits = [
  "Switch between Table, Capsule, and Calendar views without losing your filters",
  "Give managers full visibility with Tasks (Everyone), without extra setup",
  "Break work into sub-tasks and still track it under one parent",
  "Keep notes, history, and client context on the task itself, not in chat",
  "Turn closed tasks into billing records automatically, by client",
];

export default function TaskManagement() {
  useEffect(() => {
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
  }, []);

  return (
    <>
      <Helmet>
        <title>Task Management | Simplifyte</title>
      </Helmet>
      <HeaderMenu />
      <div className="TaskMgmt_class_main">
        {/* Hero */}
        <section className="TaskMgmt_class_hero">
          <div className="TaskMgmt_class_container">
            <div className="TaskMgmt_class_hero_content">
              <div className="TaskMgmt_class_icon_box">
                <ListChecks size={34} />
              </div>

              <h1 className="TaskMgmt_class_heading">
                Task management, built for how professional teams actually
                work
              </h1>

              <p className="TaskMgmt_class_subheading">
                Table, Capsule, or Calendar — see your work the way you
                think, with every note, sub-task, and history behind a
                single click.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="TaskMgmt_class_features_section">
          <div className="TaskMgmt_class_container">
            <h2 className="TaskMgmt_class_section_title TaskMgmt_class_center">
              Everything a task needs, nothing it doesn't
            </h2>

            <div className="TaskMgmt_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="TaskMgmt_class_feature_card">
                  <div className="TaskMgmt_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="TaskMgmt_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="TaskMgmt_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Views Showcase */}
        <section className="TaskMgmt_class_showcase_section">
          <div className="TaskMgmt_class_container">
            <h2 className="TaskMgmt_class_section_title TaskMgmt_class_center">
              However you like to work
            </h2>
            <p className="TaskMgmt_class_showcase_subtitle">
              The same tasks, three different ways to see them — switch
              anytime, no setup required.
            </p>

            <div className="TaskMgmt_class_showcase_list">
              {viewsShowcase.map((item, index) => (
                <div
                  key={index}
                  className={`TaskMgmt_class_showcase_row ${
                    index % 2 === 1 ? "TaskMgmt_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="TaskMgmt_class_showcase_text">
                    <span className="TaskMgmt_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="TaskMgmt_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="TaskMgmt_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="TaskMgmt_class_showcase_media">
                    <div className="TaskMgmt_class_showcase_frame_outer">
                      <div className="TaskMgmt_class_showcase_frame_inner">
                        <img
                          src={item.image}
                          alt={item.alt}
                          loading="lazy"
                          className="TaskMgmt_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Detail / Context Showcase */}
        <section className="TaskMgmt_class_showcase_section TaskMgmt_class_showcase_section_alt">
          <div className="TaskMgmt_class_container">
            <h2 className="TaskMgmt_class_section_title TaskMgmt_class_center">
              Full context, every time you open a task
            </h2>
            <p className="TaskMgmt_class_showcase_subtitle">
              Details, notes, sub-tasks, history, and billing — all one
              click away from the task itself.
            </p>

            <div className="TaskMgmt_class_showcase_list">
              {detailShowcase.map((item, index) => (
                <div
                  key={index}
                  className={`TaskMgmt_class_showcase_row ${
                    index % 2 === 1 ? "TaskMgmt_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="TaskMgmt_class_showcase_text">
                    <span className="TaskMgmt_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="TaskMgmt_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="TaskMgmt_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="TaskMgmt_class_showcase_media">
                    <div className="TaskMgmt_class_showcase_frame_outer">
                      <div className="TaskMgmt_class_showcase_frame_inner">
                        <img
                          src={item.image}
                          alt={item.alt}
                          loading="lazy"
                          className="TaskMgmt_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="TaskMgmt_class_benefits_section">
          <div className="TaskMgmt_class_container">
            <div className="TaskMgmt_class_benefits_wrapper">
              <h2 className="TaskMgmt_class_section_title TaskMgmt_class_center">
                Why teams run their work on Simplifyte
              </h2>

              <div className="TaskMgmt_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="TaskMgmt_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="TaskMgmt_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="TaskMgmt_class_cta_section">
          <div className="TaskMgmt_class_container">
            <div className="TaskMgmt_class_cta_box">
              <h2 className="TaskMgmt_class_cta_title">
                Ready to see your work this clearly?
              </h2>

              <p className="TaskMgmt_class_cta_text">
                Join teams who stopped losing track of tasks between five
                different tools.
              </p>

              <button
                className="TaskMgmt_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
}
